import * as Mapbox from 'mapbox-gl'
import { forkJoin, Observable, of, Subscription } from 'rxjs'
import { switchMap } from 'rxjs/operators'

import * as THREE from 'three'

import { AfterContentInit, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core'
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { S3ObjectRef } from '@classes/FeatureProperty'
import { FileReference } from '@classes/FileReference'
import { styles } from '@classes/MapSpace'
import { Scene } from '@classes/Scene'
import { sceneLoadMap } from '@classes/SceneManager'
import { SceneProperty } from '@classes/SceneProperty'
import { CommandService } from '@services/command.service'
import { CopyService } from '@services/copy.service'
import { EnvironmentManagerService, getRadians } from '@services/environment-manager.service'
import { FeatureService } from '@services/feature.service'
import { FileReferenceService } from '@services/file-reference.service'
import { LoadingService } from '@services/loading.service'
import { ProjectService } from '@services/project.service'
import { SceneService } from '@services/scene.service'
import { ThumbnailService } from '@services/thumbnail.service'
import { ToastService } from '@services/toast.service'
import { TooltipService } from '@services/tooltip.service'

import { isIdentityRotation, parseRotationOffset, serializeRotationOffset } from '../../../utils/rotation-offsets'

@Component({
    selector: 'shared-edit-scene',
    templateUrl: './edit-scene.component.html',
    styleUrls: ['./edit-scene.component.css'],
    standalone: false
})
export class EditSceneComponent implements OnChanges, OnDestroy {
    @Input() scene: Scene
    private _subscriptions: Subscription[] = []
    public mapStyles = styles
    public orderedPropertyKeys = ["longitude", "latitude", "zoom", "pitch", "bearing"]
    public skyboxOptions = [
        { display: 'Local Time', value: 'local' },
        { display: 'Sunrise', value: 'sunrise' },
        { display: 'Morning', value: 'morning' },
        { display: 'Afternoon', value: 'afternoon' },
        { display: 'Evening', value: 'evening' },
        { display: 'Sunset', value: 'sunset' },
        { display: 'Night', value: 'night' },
    ]

    public sceneForm: UntypedFormGroup = new UntypedFormGroup({
        name: new UntypedFormControl(),
        type: new UntypedFormControl(),
        sceneOfInterest: new UntypedFormControl('', { updateOn: 'change' }),
        properties: new UntypedFormArray([])
    }, { updateOn: 'blur' })

    public showAdvanced = false

    get properties() { return this.sceneForm.get('properties') as UntypedFormArray }
    get bearing() { return this.getPropertyFormGroup('bearing') }
    get bounds() { return this.getPropertyFormGroup('bounds') }
    get file() { return this.getPropertyFormGroup('file') }
    get longitude() { return this.getPropertyFormGroup('longitude') }
    get latitude() { return this.getPropertyFormGroup('latitude') }
    get overlap() { return this.getPropertyFormGroup('overlap') }
    get pitch() { return this.getPropertyFormGroup('pitch') }
    get style() { return this.getPropertyFormGroup('style') }
    get terrain() { return this.getPropertyFormGroup('terrain') }
    get zoom() { return this.getPropertyFormGroup('zoom') }
    get timeOfDay() { return this.getPropertyFormGroup('timeOfDay') }

    get fileReference() {
        const prop = this.scene.properties.find(p => p.key == 'file')
        return prop?.fileReference ?? prop?.s3ObjectReference ?? null
    }
    get sceneManager() { return this.envManager.sceneManager }
    get mapSpace() { return this.envManager.mapSpace }
    get map() { return this.mapSpace?.map }
    get camera() { return this.envManager.modelSpace.camera }
    get orbitControls() { return this.envManager.modelSpace.orbitControls }
    get sceneOverlap() { return this.scene.properties.find(p => p.key === 'overlap').value }

    constructor(
        private _fileReferenceService: FileReferenceService,
        private _loadingService: LoadingService,
        private _projectService: ProjectService,
        private _toastService: ToastService,
        public copyService: CopyService,
        public envManager: EnvironmentManagerService,
        public featureService: FeatureService,
        public sceneService: SceneService,
        public thumbnailService: ThumbnailService,
        public tooltipService: TooltipService,
        public commandService: CommandService
    ) { }
    ngOnChanges(changes: SimpleChanges): void {
        // Recreate the forms
        this.sceneForm.patchValue({
            name: this.scene.name,
            type: this.scene.type,
            sceneOfInterest: this.scene.sceneOfInterest,
        }, { emitEvent: false })

        this.scene.properties.sort((a, b) => {
            if (a.key > b.key) return -1;
            if (b.key > a.key) return 1;
            return 0;
        })

        this.properties.controls = this.scene.properties.map(p => this.newPropertyForm(p))
        this.tooltipService.intializeTooltips()
    }

    newPropertyForm(property: SceneProperty): UntypedFormGroup {
        let value: AbstractControl

        if (property.type == 'vector3') {
            value = new UntypedFormArray([new UntypedFormControl(0), new UntypedFormControl(0), new UntypedFormControl(0)])
            value.patchValue(property.value)
        } else if (property.key == 'timeOfDay') {
            value = new UntypedFormControl(property.value, { updateOn: 'change' })
        } else if (property.type == 'boolean' || property.key == 'style') value = new UntypedFormControl(property.value, { updateOn: 'change' })
        else value = new UntypedFormControl(property.value)

        return new UntypedFormGroup({
            id: new UntypedFormControl(property.id),
            sceneID: new UntypedFormControl(property.sceneID),
            fileReferenceID: new UntypedFormControl(property.fileReferenceID),
            type: new UntypedFormControl(property.type),
            key: new UntypedFormControl(property.key),
            value: value
        }, { updateOn: 'blur' })
    }

    getPropertyFormGroup(key: string) {
        return this.properties.controls.find(prop => prop.get("key").value == key) as UntypedFormGroup
    }

    downloadBackgroundFile(ref: FileReference | S3ObjectRef) {
        const prop = this.scene.properties.find(p => p.key == 'file')
        if (prop?.s3ObjectReference) {
            this._fileReferenceService.downloadS3File(prop.s3ObjectReference.id, prop.s3ObjectReference.filename).subscribe()
        } else if (prop?.fileReference) {
            this._fileReferenceService.downloadFile(prop.fileReference, this._projectService.currentProject.id).subscribe()
        }
    }

    replaceBackgroundFile(file: File) {
        if (file) {
            const scene = this.sceneService.selectedScene
            const imageProperty = scene.properties.find(p => p.type == "image")
            if (!imageProperty) return

            const sceneLoad = sceneLoadMap.get(scene.id)
            sceneLoad.backgroundLoaded = false

            // Always use S3 for new uploads — handles EFS→S3 migration on backend
            this.sceneService.replaceSceneImageS3(imageProperty.id, file).subscribe()
        }
    }

    updateSceneAttribute(attribute: string) {
        this.sceneService.getScene(this.scene.id).subscribe(scene => {
            const oldSceneAttribute = scene[attribute]
            this.commandService.update.scene(this.scene, attribute, this.sceneForm.get(attribute).value, oldSceneAttribute).subscribe()
        })
    }

    setStartingOrientation() {
        // Save original values before updating
        const properties: SceneProperty[] = []
        const previousProperties: SceneProperty[] = []
        const targetProperty = this.scene.targetPosition
        const cameraProperty = this.scene.cameraPosition
        const targetPosition: [number, number, number] = this.orbitControls.target.toArray()
        const cameraPosition: [number, number, number] = this.camera.position.toArray()

        if (targetProperty && JSON.stringify(targetProperty.value) != JSON.stringify(targetPosition)) {
            // Clone previous property for revert
            previousProperties.push({ ...targetProperty, value: Array.isArray(targetProperty.value) ? [...targetProperty.value] : targetProperty.value })
            targetProperty.value = targetPosition
            properties.push({ ...targetProperty })
        }

        if (cameraProperty && JSON.stringify(cameraProperty.value) != JSON.stringify(cameraPosition)) {
            previousProperties.push({ ...cameraProperty, value: Array.isArray(cameraProperty.value) ? [...cameraProperty.value] : cameraProperty.value })
            cameraProperty.value = cameraPosition
            properties.push({ ...cameraProperty })
        }

        if (properties.length > 0) {
            this.commandService.update.sceneProperties(properties, previousProperties).subscribe(() => {
                this._toastService.toast({ title: 'Update', message: 'Updated Scene Orientation', color: 'green', autohide: true })
            })
        }
    }

    setMapsStartingOrientation() {
        if (!this.map) return

        const updatedProperties: SceneProperty[] = []
        const previousProperties: SceneProperty[] = []
        const createdProperties: SceneProperty[] = []
        const center = this.map.getCenter()

        const bounds = this.map.getBounds()
        const boundingBox = {
            northEast: bounds.getNorthEast().toArray(),
            southWest: bounds.getSouthWest().toArray(),
        }

        const checkPropertyChanged = (key: string, type: SceneProperty['type'], value: any) => {
            const propertyGroup = this.getPropertyFormGroup(key)
            const existingProperty = this.scene.properties.find(p => p.key === key)

            if (propertyGroup || existingProperty) {
                const currentValue = propertyGroup?.get("value").value ?? existingProperty.value

                if (JSON.stringify(currentValue) != JSON.stringify(value)) {
                    const previousProperty = { ...(propertyGroup?.value ?? existingProperty), value: this.clonePropertyValue(currentValue) } as SceneProperty
                    const updatedProperty = { ...(propertyGroup?.value ?? existingProperty), value } as SceneProperty

                    previousProperties.push(previousProperty)
                    updatedProperties.push(updatedProperty)

                    propertyGroup?.get("value").setValue(value)
                    if (existingProperty) existingProperty.value = value
                }
            } else {
                createdProperties.push(new SceneProperty(this.scene.id, type, key, value))
            }
        }

        checkPropertyChanged('bounds', 'string', boundingBox)
        checkPropertyChanged('bearing', 'decimal', this.map.getBearing())
        checkPropertyChanged('longitude', 'decimal', center.lng)
        checkPropertyChanged('latitude', 'decimal', center.lat)
        checkPropertyChanged('pitch', 'decimal', this.map.getPitch())
        checkPropertyChanged('zoom', 'decimal', this.map.getZoom())

        if (updatedProperties.length > 0 || createdProperties.length > 0) {
            const update$ = updatedProperties.length > 0
                ? this.commandService.update.sceneProperties(updatedProperties, previousProperties)
                : of([])
            const create$ = createdProperties.length > 0
                ? forkJoin(createdProperties.map(property => this.sceneService.createSceneProperty(property)))
                : of([])

            forkJoin([update$, create$]).subscribe(() => {
                this._toastService.toast({ title: 'Update', message: 'Updated Scene Orientation', color: 'green', autohide: true })
            })
        }
    }

    private clonePropertyValue<T>(value: T): T {
        if (value === undefined || value === null) return value

        return JSON.parse(JSON.stringify(value))
    }

    updateSunPosition(timeOfDayForm: UntypedFormGroup) {
        const timeOfDay: string = timeOfDayForm.value.value
        const latitude: number = this.latitude.value.value
        const longitude: number = this.longitude.value.value
        const sunPos = this.envManager.getSunPosition(timeOfDay, longitude, latitude)

        this.sceneService.updateSceneProperty(timeOfDayForm.value).subscribe(() =>
            this.envManager.map.setPaintProperty('sky', 'sky-atmosphere-sun', sunPos))
    }

    updateTerrain(terrainProperty: SceneProperty) {
        const featureOffsets = this.featureService.features.map(
            feature => { // Finds the difference between the Map's Altitude and the Feature's
                const coordinates = new Mapbox.LngLat(feature.position[0], feature.position[1])
                const mapAltitude = this.map.queryTerrainElevation(coordinates)
                const altitudeDifference = feature.position[2] - mapAltitude

                return { featureID: feature.id, altitudeDifference: altitudeDifference }
            }
        ) as { featureID: number, altitudeDifference: number }[]

        this._loadingService.setLoaded(false) // Loading screen on until all Feature positions have updated

        this.sceneService.updateSceneProperty(terrainProperty).pipe(
            switchMap(() => new Observable(obs => {
                this.map.once('idle', (load) => {
                    obs.next(load)
                    obs.complete()
                })
            })),
            switchMap(() => forkJoin(
                this.featureService.features.map(feature => {
                    const coordinates = new Mapbox.LngLat(feature.position[0], feature.position[1])
                    const mapAltitude = this.map.queryTerrainElevation(coordinates)
                    const altitudeDifference = featureOffsets.find(fo => fo.featureID == feature.id)?.altitudeDifference
                    const newAltitude = mapAltitude + altitudeDifference

                    feature.position[2] = newAltitude

                    return this.featureService.updateFeature(feature)
                })
            ))
        ).subscribe(() => this._loadingService.setLoaded(true))
    }

    updateOverlapProperty(overlapProperty: SceneProperty) {
        const oldOverlapProperty = this.scene.properties.find(p => p.key === 'overlap')
        this.commandService.update.sceneProperty(overlapProperty, oldOverlapProperty).subscribe(property => {
            const overlap = overlapProperty.value as boolean
            const layer = 'pointLayer'

            this.map.setLayoutProperty(layer, 'icon-allow-overlap', !overlap)
            this.map.setLayoutProperty(layer, 'text-allow-overlap', !overlap)
        })

    }
    updateSceneProperty(sceneProperty: SceneProperty) {
        this.sceneService.updateSceneProperty(sceneProperty).subscribe()
    }

    /** Current scene-level rotation offset (identity if not yet set). */
    get sceneRotationOffsetMatrix(): THREE.Matrix4 {
        return parseRotationOffset(this.scene.rotationOffset?.value)
    }

    /** Persist a new scene-level rotation offset. Creates the property row if absent;
     *  clears it (via empty-string update) when the user resets to identity.
     *
     *  Whenever the scene-level offset changes — to a new value or back to
     *  identity — any per-viewpoint *identity* overrides in this scene are
     *  cleared. An identity per-viewpoint row means "Reset relative to the
     *  scene state at the time of the click," and it's only meaningful until
     *  the scene state changes. Custom (non-identity) per-viewpoint rotations
     *  are explicit user intent and are preserved across scene changes. */
    onSceneRotationOffsetChange(matrix: THREE.Matrix4) {
        const existing = this.scene.rotationOffset
        const serialized = isIdentityRotation(matrix) ? '' : serializeRotationOffset(matrix)

        if (existing) {
            existing.value = serialized
            this.sceneService.updateSceneProperty(existing).subscribe(() => {
                this._clearIdentityViewpointOverridesInScene()
            })
        } else if (serialized !== '') {
            const created = new SceneProperty(this.scene.id, 'string', 'rotationOffset', serialized)
            this.sceneService.createSceneProperty(created).subscribe(saved => {
                this.scene.properties.push(saved)
                this._clearIdentityViewpointOverridesInScene()
            })
        }
    }

    /** Clear identity-only per-viewpoint rotationOffset overrides in the current
     *  scene. Custom (non-identity) per-viewpoint rotations are preserved. */
    private _clearIdentityViewpointOverridesInScene() {
        const featuresInScene = this.featureService.features.filter(f => f.sceneID === this.scene.id)
        for (const f of featuresInScene) {
            const prop = f.rotationOffset
            if (!prop || !prop.value) continue
            const m = parseRotationOffset(prop.value)
            if (isIdentityRotation(m)) {
                prop.value = ''
                this.featureService.updateFeatureProperty(prop).subscribe()
            }
        }
    }

    /** Updates the north angle with the current camera angle. */
    setNorthAngle() {
        if (this.scene.type != '360 Image') return

        const cameraPosition = this.camera.position.clone()
        const cameraAngle = getRadians(cameraPosition.x, cameraPosition.z)
        const northProperty = this.scene.properties.find(p => p.key == 'northAngle')

        northProperty.value = cameraAngle

        this.sceneService.updateSceneProperty(northProperty).subscribe()
    }

    setSceneThumbnail() {
        this.thumbnailService.updateSceneThumbnail()
    }

    toggleCollapse() {
        this.showAdvanced = !this.showAdvanced
    }

    ngOnDestroy() {
        this._subscriptions.forEach(sub => sub.unsubscribe())
    }
}
